import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { Badge } from "@/components/ui/Badge";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { ApiError } from "@/lib/api";
import { usePageBreadcrumb } from "@/lib/navigationTrailContext";

import {
  fetchCbpMonthlyStatements,
  type CbpMonthlyStatementDocument,
  type CbpMonthlyStatementMonth,
} from "./apCbpMonthlyStatementsApi";

function formatIsoDate(isoDate: string, monthOnly = false): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (match == null) {
    return isoDate;
  }
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    month: "long",
    day: monthOnly ? undefined : "numeric",
    year: "numeric",
  }).format(date);
}

function formatMoney(amount: string, currency: string): string {
  const parsed = Number.parseFloat(amount);
  if (!Number.isFinite(parsed)) {
    return amount;
  }
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(parsed);
  } catch {
    return `${parsed.toFixed(2)} ${currency}`;
  }
}

function documentStatus(document: CbpMonthlyStatementDocument): string {
  if (document.tsh_workflow_status_label != null && document.tsh_workflow_status_label !== "") {
    return `${document.cbp_status_label}, ${document.tsh_workflow_status_label}`;
  }
  return document.cbp_status_label;
}

type CbpDocumentSortKey =
  | "cbp_vendor_invoice_number"
  | "amount"
  | "status"
  | "shipper_vendor_invoice_number"
  | "shipper_status_label"
  | "gipi_number"
  | "gipi_status";

const CBP_STATEMENT_COLUMNS: HubDataTableColumn<CbpMonthlyStatementDocument, CbpDocumentSortKey>[] = [
  {
    id: "cbp_vendor_invoice_number",
    header: "CBP invoice",
    sortKey: "cbp_vendor_invoice_number",
    sortValue: (row) => row.cbp_vendor_invoice_number,
    cell: (row) => (
      <Link
        className="font-medium text-brand-700 hover:underline"
        to={`/accounting/ap/${encodeURIComponent(row.cbp_invoice_id)}/edit`}
      >
        {row.cbp_vendor_invoice_number}
      </Link>
    ),
  },
  {
    id: "amount",
    header: "Amount",
    sortKey: "amount",
    align: "right",
    overflow: "nowrap",
    sortValue: (row) => Number.parseFloat(row.amount),
    cell: (row) => formatMoney(row.amount, row.currency),
  },
  {
    id: "status",
    header: "Status",
    sortKey: "status",
    sortValue: (row) => documentStatus(row),
    cell: (row) => (
      <Badge tone={row.cbp_status === "submitted" ? "success" : "warning"}>{documentStatus(row)}</Badge>
    ),
  },
  {
    id: "shipper_vendor_invoice_number",
    header: "Shipper invoice",
    sortKey: "shipper_vendor_invoice_number",
    sortValue: (row) => row.shipper_vendor_invoice_number,
    cell: (row) => (
      <Link
        className="text-brand-700 hover:underline"
        to={`/accounting/ap/${encodeURIComponent(row.shipper_invoice_id)}/edit`}
      >
        {row.shipper_vendor_invoice_number}
      </Link>
    ),
  },
  {
    id: "shipper_status_label",
    header: "Shipper status",
    sortKey: "shipper_status_label",
    sortValue: (row) => row.shipper_status_label,
    cell: (row) => row.shipper_status_label,
  },
  {
    id: "gipi_number",
    header: "GIPI",
    sortKey: "gipi_number",
    sortValue: (row) => row.gipi_number ?? "",
    cell: (row) =>
      row.gipi_id != null ? (
        <Link
          className="text-brand-700 hover:underline"
          to={`/mrp/purchase-invoices/${encodeURIComponent(row.gipi_id)}`}
        >
          {row.gipi_number ?? row.gipi_id}
        </Link>
      ) : (
        ""
      ),
  },
  {
    id: "gipi_status",
    header: "GIPI status",
    sortKey: "gipi_status",
    sortValue: (row) => row.gipi_status ?? "",
    cell: (row) => row.gipi_status ?? "",
  },
];

function MonthStatements(props: { month: CbpMonthlyStatementMonth }): JSX.Element {
  const month = props.month;
  const totalLabel = month.totals
    .map((total) => formatMoney(total.amount, total.currency))
    .join(", ");
  return (
    <HubDataTable
      fillViewport={false}
      className="max-h-80"
      title={formatIsoDate(month.statement_month, true)}
      headerExtra={
        <p className="text-sm text-slate-700">
          Due {formatIsoDate(month.due_on)}
          <span className="ml-3 font-medium tabular-nums text-slate-900">{totalLabel}</span>
        </p>
      }
      columns={CBP_STATEMENT_COLUMNS}
      rows={month.documents}
      rowKey={(row) => row.cbp_invoice_id}
      empty="No invoices in this statement month."
    />
  );
}

export function ApCbpMonthlyStatementsPage(): JSX.Element {
  usePageBreadcrumb("Periodic monthly statements");
  const listQuery = useQuery({
    queryKey: ["accounting", "cbp-monthly-statements"],
    queryFn: fetchCbpMonthlyStatements,
  });
  const months = listQuery.data?.months ?? [];
  const errorMessage =
    listQuery.error instanceof ApiError
      ? listQuery.error.message
      : listQuery.error instanceof Error
        ? listQuery.error.message
        : null;

  return (
    <div className="space-y-4">
      <PageTitleWithHelp hoverHint="Customs and Border Protection Periodic Monthly Statement. Each entry month is debited on the 15th business day of the following month. The total is the sum of the saved CBP invoices for that month.">
        Periodic monthly statements
      </PageTitleWithHelp>

      {listQuery.isLoading ? (
        <p className="flex items-center gap-2 text-sm text-slate-600">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Loading statements
        </p>
      ) : null}
      {errorMessage != null ? <p className="text-sm text-red-700">{errorMessage}</p> : null}
      {!listQuery.isLoading && errorMessage == null && months.length === 0 ? (
        <p className="text-sm text-slate-600">No CBP monthly statements yet.</p>
      ) : null}
      <div className="space-y-4">
        {months.map((month) => (
          <MonthStatements key={`${month.statement_month}|${month.due_on}`} month={month} />
        ))}
      </div>
    </div>
  );
}
